import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE,
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !error.config?._skipAuthRedirect) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

/**
 * Fetch a PDF from an authed backend endpoint and open it in a new tab.
 * Use this instead of `window.open('/api/...')` when the endpoint requires a
 * Bearer token — the browser drops the Authorization header on a raw window
 * navigation, so the server returns 401 and the tab shows a JSON error.
 *
 * @param {string} path  Backend path relative to `/api` (e.g. `/pennylane-invoices/123/pdf`)
 * @returns {Promise<Window|null>} the opened window (or null if blocked by popup blocker)
 */
export const openAuthedPdf = async (path) => {
  const response = await api.get(path, { responseType: 'blob' });
  const blob = new Blob([response.data], { type: 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  const win = window.open(url, '_blank');
  // Revoke the URL after the tab has had time to load the PDF.
  setTimeout(() => window.URL.revokeObjectURL(url), 60000);
  return win;
};

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  getRoles: () => api.get('/auth/roles'),
};

// Users API (Admin only)
export const usersAPI = {
  list: (params) => api.get('/users', { params }),
  get: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  resetPassword: (id, password) => api.post(`/users/${id}/reset-password`, { password }),
  getRoles: () => api.get('/roles'),
};

// Clients API
export const clientsAPI = {
  list: (params) => api.get('/clients', { params }),
  get: (id) => api.get(`/clients/${id}`),
  create: (data) => api.post('/clients', data),
  update: (id, data) => api.put(`/clients/${id}`, data),
  delete: (id) => api.delete(`/clients/${id}`),
  bulkDelete: (ids) => api.request({
    method: 'DELETE',
    url: '/clients/bulk/delete',
    data: ids,
    headers: { 'Content-Type': 'application/json' }
  }),
  syncPennylane: (id) => api.post(`/clients/${id}/sync-pennylane`),
  syncPennylaneAll: () => api.post('/clients/sync-pennylane-all'),
  syncHubspot: (id) => api.post(`/clients/${id}/sync-hubspot`),
  portalImpersonate: (id) => api.post(`/client-portal/impersonate/${id}`),
};

// Contracts API
export const contractsAPI = {
  list: (params) => api.get('/contracts', { params }),
  get: (id) => api.get(`/contracts/${id}`),
  create: (data) => api.post('/contracts', data),
  update: (id, data) => api.put(`/contracts/${id}`, data),
  delete: (id) => api.delete(`/contracts/${id}`),
  bulkDelete: (ids) => api.delete('/contracts/bulk/delete', { data: ids }),
  terminate: (id, data) => api.post(`/contracts/${id}/terminate`, data),
  getEquipmentsForTermination: (id) => api.get(`/contracts/${id}/equipments-for-termination`),
  checkUnpaidSuspension: () => api.post('/contracts/check-unpaid-suspension'),
  unsuspend: (id) => api.post(`/contracts/${id}/unsuspend`),
};

// Equipments API
export const equipmentsAPI = {
  list: (params) => api.get('/equipments', { params }),
  get: (id) => api.get(`/equipments/${id}`),
  create: (data) => api.post('/equipments', data),
  update: (id, data) => api.put(`/equipments/${id}`, data),
  delete: (id) => api.delete(`/equipments/${id}`),
  bulkDelete: (ids) => api.delete('/equipments/bulk/delete', { data: ids }),
  bulkDuplicate: (items, duplicateSubscriptions = true) => api.post('/equipments/bulk/duplicate', { items, duplicate_subscriptions: duplicateSubscriptions }),
  bulkAssociateTemplate: (ids, template_id) => api.put('/equipments/bulk/associate-template', { ids, template_id }),
  bulkChangeCategory: (ids, type_equipement) => api.put('/equipments/bulk/change-category', { ids, type_equipement }),
  bulkMerge: (master_id, ids_to_merge) => api.post('/equipments/bulk/merge', { master_id, ids_to_merge }),
  bulkGroupIntoBundle: (master_id, ids_to_group) => api.post('/equipments/bulk/group-into-bundle', { master_id, ids_to_group }),
  backfillSousCategorie: () => api.post('/equipments/backfill-sous-categorie'),
};

// Subscriptions API
export const subscriptionsAPI = {
  list: (params) => api.get('/subscriptions', { params }),
  get: (id) => api.get(`/subscriptions/${id}`),
  create: (data) => api.post('/subscriptions', data),
  update: (id, data) => api.put(`/subscriptions/${id}`, data),
  delete: (id) => api.delete(`/subscriptions/${id}`),
  bulkDelete: (ids) => api.delete('/subscriptions/bulk/delete', { data: ids }),
  importBulk: (data) => api.post('/subscriptions/import-bulk', data),
  backfillTemplateIds: () => api.post('/subscriptions/backfill-template-ids'),
};

// Indexes API
export const indexesAPI = {
  list: (params) => api.get('/indexes', { params }),
  create: (data) => api.post('/indexes', data),
  update: (id, data) => api.put(`/indexes/${id}`, data),
  delete: (id) => api.delete(`/indexes/${id}`),
  getMissing: () => api.get('/indexes/missing'),
  bulkUpdate: (data) => api.put('/indexes/bulk', data),
  bulkDelete: (data) => api.post('/indexes/bulk-delete', data),
  getWorksheet: () => api.get('/indexes/worksheet'),
  saveWorksheet: (data) => api.post('/indexes/worksheet/save', data),
};

// Counter Requests API
export const counterRequestsAPI = {
  list: (params) => api.get('/counter-requests', { params }),
  get: (id) => api.get(`/counter-requests/${id}`),
  create: (data) => api.post('/counter-requests', data),
  delete: (id) => api.delete(`/counter-requests/${id}`),
  bulkDelete: (ids) => api.delete('/counter-requests', { data: ids }),
  resend: (id) => api.post(`/counter-requests/${id}/resend`),
  getFile: (id) => api.get(`/counter-requests/${id}/file`),
  getViewUrl: (id) => {
    const token = localStorage.getItem('token') || '';
    return `${process.env.REACT_APP_BACKEND_URL}/api/counter-requests/${id}/file/view?token=${encodeURIComponent(token)}`;
  },
  process: (id, data) => api.post(`/counter-requests/${id}/process`, data),
  sendReminders: (payload = {}) => api.post('/counter-requests/send-reminders', payload),
  sendSingleReminder: (id) => api.post(`/counter-requests/${id}/send-reminder`),
  sendReminderCancellation: (payload) => api.post('/counter-requests/send-reminder-cancellation', payload),
  exportHistoryPdf: (id) => api.get(`/counter-requests/${id}/export-history`, { responseType: 'blob' }),
  generateMonthly: () => api.post('/counter-requests/generate-monthly'),
  getPendingSubmissions: () => api.get('/counter-requests/pending-submissions'),
  // Import CSV
  importCsv: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/counters/import-csv', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  downloadTemplate: () => api.get('/counters/import-template', { responseType: 'blob' }),
  cancelImport: (batchId) => api.delete(`/counters/import/${batchId}`),
  // Queue-based import
  importCsvQueue: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/counters/import-csv-queue', formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  importCsvQueueStatus: (jobId) => api.get(`/counters/import-csv-queue/status`, { params: { job_id: jobId } }),
  cancelImportQueue: () => api.post('/counters/import-csv-queue/cancel'),
  // Counters history
  getCountersHistory: (params) => api.get('/counters/history', { params }),
  // Period history & Overdue
  getPeriodHistory: (params) => api.get('/counter-requests/period-history', { params }),
  getOverdueClients: (monthsThreshold) => api.get('/counter-requests/overdue-clients', { params: { months_threshold: monthsThreshold } }),
  sendOverdueWarning: (clientId, monthsOverdue) => api.post(`/counter-requests/send-overdue-warning/${clientId}`, null, { params: { months_overdue: monthsOverdue } }),
  sendBulkOverdueWarnings: (monthsThreshold) => api.post('/counter-requests/send-bulk-overdue-warnings', null, { params: { months_threshold: monthsThreshold } }),
  exportClientHistory: (clientId, format) => api.get(`/counter-requests/export-client-history/${clientId}`, { params: { format }, responseType: 'blob' }),
  // Overdue config
  getOverdueConfig: () => api.get('/overdue-config'),
  getOverdueHistory: (level) => api.get('/overdue-config/history', { params: level ? { level } : {} }),
  updateOverdueConfig: (params) => api.put('/overdue-config', null, { params }),
  updateReminderLevel: (level, params) => api.put(`/overdue-config/level/${level}`, null, { params }),
  getOverdueStats: () => api.get('/overdue-config/stats'),
  sendRemindersForLevel: (level, dryRun = false) => api.post(`/overdue-config/send-level/${level}`, null, { params: { dry_run: dryRun } }),
  resetOverdueConfig: () => api.post('/overdue-config/reset'),
};

// Ticket Attachments API
export const attachmentsAPI = {
  upload: (ticketId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/attachments/${ticketId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
  },
  list: (ticketId) => api.get(`/attachments/${ticketId}`),
  download: (ticketId, filename) => api.get(`/attachments/${ticketId}/${filename}`, { responseType: 'blob' }),
  delete: (ticketId, filename) => api.delete(`/attachments/${ticketId}/${filename}`),
};

// Invoices API
export const invoicesAPI = {
  list: (params) => api.get('/invoices', { params }),
  get: (id) => api.get(`/invoices/${id}`),
  create: (data) => api.post('/invoices', data),
  delete: (id) => api.delete(`/invoices/${id}`),
  cancel: (id) => api.post(`/invoices/${id}/cancel`),
  validate: (id, force = false) => api.put(`/invoices/${id}/validate${force ? '?force=true' : ''}`),
  validateBatch: (invoiceIds) => api.post('/invoices/validate-batch', invoiceIds),
  sendToPennylane: (id) => api.post(`/invoices/${id}/send-to-pennylane`),
  sendToPennylaneBatch: (invoiceIds) => api.post('/invoices/send-to-pennylane-batch', { invoice_ids: invoiceIds }),
  getPennylaneSendJob: (jobId) => api.get(`/invoices/pennylane-send-jobs/${jobId}`),
  getPennylanePdf: (id) => api.get(`/invoices/${id}/pennylane-pdf`),
  syncPennylaneStatus: (pennylaneIds) => api.post('/invoices/sync-pennylane-status', pennylaneIds || null),
  syncFromPennylane: (clientId) => api.post(`/invoices/sync-from-pennylane/${clientId}`),
  listPennylaneInvoices: (clientId, searchByName = false) => api.get(`/invoices/pennylane-list/${clientId}?search_by_name=${searchByName}`),
  importFromPennylane: (clientId, pennylaneIds) => api.post(`/invoices/import-from-pennylane/${clientId}`, pennylaneIds),
  // Supplier invoices (for leaser invoices)
  searchSupplierInvoices: (invoiceNumber) => api.get('/pennylane/supplier-invoices', { params: { invoice_number: invoiceNumber } }),
  // PDF generation
  downloadPDF: (id) => api.get(`/invoices/${id}/pdf`, { responseType: 'blob' }),
  // Old billing endpoints (kept for compatibility)
  getBillingOverview: (mois, annee) => api.get('/invoices/billing-overview', { params: { mois, annee } }),
  generateBatch: (params, contractIds) => api.post('/invoices/generate-batch', contractIds, { params }),
};

// Billing Worksheet API (New)
export const billingAPI = {
  // Get worksheet data (tabular view)
  getWorksheet: (params) => api.get('/billing/worksheet', { params, timeout: 120000 }),
  refreshWorksheet: (params) => api.post('/billing/worksheet/refresh', null, { params, timeout: 180000 }),
  estimateAll: (params, data = null) => api.post('/billing/estimate-all', data, { params, timeout: 120000 }),
  generateAsync: (params, body) => api.post('/billing/generate-invoices-async', body, { params }),
  getJobStatus: (jobId) => api.get(`/billing/jobs/${jobId}`),
  // Update a counter reading
  updateReading: (subscriptionId, data, params) => 
    api.post(`/billing/update-reading/${subscriptionId}`, data, { params }),
  // Delete/reset a counter reading
  deleteReading: (subscriptionId, params) =>
    api.delete(`/billing/reading/${subscriptionId}`, { params }),
  // Freeze K-PAX readings at a specific date (global)
  freezeKpax: (params) =>
    api.post('/billing/freeze-kpax', null, { params }),
  // Freeze a single K-PAX reading
  freezeKpaxSingle: (subscriptionId, params) =>
    api.post(`/billing/freeze-kpax-single/${subscriptionId}`, null, { params }),
  // Validate "forfait seul" billing for a batch of indexed subscriptions
  // that landed in the "manquant" bucket because no consumption could be
  // computed (silent machine, no reading, flat counter, etc.). Writes a
  // manual reading at compteur_debut -> next worksheet load shows them OK.
  validateForfaitSeul: (subscriptionIds, params) =>
    api.post('/billing/validate-forfait-seul', {
      subscription_ids: subscriptionIds,
      mois: params?.mois,
      annee: params?.annee,
    }),
  // Create estimation
  estimate: (subscriptionId, params) =>
    api.post(`/billing/estimate/${subscriptionId}`, null, { params }),
  // Remove estimation
  removeEstimation: (subscriptionId, params) =>
    api.delete(`/billing/estimate/${subscriptionId}`, { params }),
  // Ré-estimation "sensibilisée" en masse : variation aléatoire ±X% autour de la moyenne (lot C)
  sensibiliserEstimations: (params, body) =>
    api.post('/billing/estimate-sensibilise', body, { params }),
  // Generate invoices from worksheet (supports selective generation)
  generateInvoices: (params, selection = {}) =>
    api.post('/billing/generate-invoices', {
      subscription_ids: selection.subscriptionIds || null,
      client_ids: selection.clientIds || null,
      include_estimated: selection.includeEstimated ?? true,
      date_facture: selection.dateFacture || null,
      segmentation: selection.segmentation || 'par_contrat',
      activite: selection.activite || null
    }, {
      params: {
        mois: params.mois,
        annee: params.annee
      }
    }),
  // Billing Periods
  getPeriods: (annee) => api.get('/billing/periods', { params: { annee } }),
  getPeriod: (mois, annee) => api.get(`/billing/periods/${mois}/${annee}`),
  closePeriod: (mois, annee, activite) =>
    api.post(`/billing/periods/${mois}/${annee}/close`, null, { params: activite ? { activite } : {} }),
  reopenPeriod: (mois, annee, activite) =>
    api.post(`/billing/periods/${mois}/${annee}/reopen`, null, { params: activite ? { activite } : {} }),
  // Bulk operations
  bulkDeleteInvoices: (invoiceIds) => 
    api.delete('/billing/invoices/bulk', { data: invoiceIds }),
  regenerateInvoice: (invoiceId) => 
    api.post(`/billing/invoices/${invoiceId}/regenerate`),
};

// Tickets API
export const ticketsAPI = {
  list: (params) => api.get('/tickets', { params }),
  get: (id) => api.get(`/tickets/${id}`),
  create: (data) => api.post('/tickets', data),
  update: (id, data) => api.put(`/tickets/${id}`, data),
  delete: (id) => api.delete(`/tickets/${id}`),
  bulkDelete: (ids) => api.delete('/tickets/bulk/delete', { data: ids }),
  updateCosts: (id, costs) => api.put(`/tickets/${id}/couts`, costs),
  getPortalUnreadCount: () => api.get('/tickets/portal-unread-count'),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard/stats'),
  getRecentActivity: () => api.get('/dashboard/recent-activity'),
};

// Integrations API
export const integrationsAPI = {
  syncHubspot: () => api.post('/integrations/hubspot/sync'),
  importKpax: () => api.post('/integrations/kpax/import-readings'),
  // Settings management
  list: () => api.get('/settings/integrations'),
  update: (id, data) => api.put(`/settings/integrations/${id}`, data),
  delete: (id) => api.delete(`/settings/integrations/${id}`),
  test: (id) => api.post(`/settings/integrations/${id}/test`),
};

// HubSpot Sync API
export const hubspotSyncAPI = {
  // Status
  getStatus: () => api.get('/hubspot/sync/status'),
  // Get pipelines
  getPipelines: () => api.get('/hubspot/sync/pipelines'),
  // Get deal property options (for filters like statut_du_contrat)
  getDealPropertyOptions: (propertyName) => api.get(`/hubspot/sync/deal-property-options/${propertyName}`),
  // List deals available for import
  listDeals: (params) => api.get('/hubspot/sync/deals', { params }),
  // Import deals as contracts
  importDeals: (dealIds, clientId) => api.post('/hubspot/sync/import', { deal_ids: dealIds, client_id: clientId }),
  // Pull data from HubSpot to contract
  pullFromHubSpot: (contractId) => api.post(`/hubspot/sync/pull/${contractId}`),
  // Push data from contract to HubSpot
  pushToHubSpot: (contractId) => api.post(`/hubspot/sync/push/${contractId}`),
  // Get linked contracts
  getLinkedContracts: () => api.get('/hubspot/sync/linked-contracts'),
  syncPrintingAll: () => api.post('/hubspot/sync/printing-all'),
  syncPrintingOne: (clientId) => api.post(`/hubspot/sync/printing-one/${clientId}`),
  getPrintingStatus: () => api.get('/hubspot/sync/printing-status'),
};

// Invoice Templates API
export const invoiceTemplatesAPI = {
  list: (params) => api.get('/invoice-templates', { params }),
  get: (id) => api.get(`/invoice-templates/${id}`),
  create: (data) => api.post('/invoice-templates', data),
  update: (id, data) => api.put(`/invoice-templates/${id}`, data),
  delete: (id) => api.delete(`/invoice-templates/${id}`),
  getCategories: () => api.get('/invoice-templates/categories'),
  getVariables: () => api.get('/invoice-templates/variables'),
  preview: (id, context) => api.post(`/invoice-templates/${id}/preview`, context),
  getByCategory: (category) => api.get(`/invoice-templates/by-category/${category}`),
  render: (category, context) => api.post('/invoice-templates/render', context, { params: { category } }),
};

// Client Portal API (admin side)
export const clientPortalAPI = {
  // Admin routes for managing portal contacts
  listContacts: (clientId) => api.get('/client-portal/admin/contacts', { params: clientId ? { client_id: clientId } : {} }),
  getContact: (id) => api.get(`/client-portal/admin/contacts/${id}`),
  createContact: (data) => api.post('/client-portal/admin/contacts', data),
  updateContact: (id, data) => api.put(`/client-portal/admin/contacts/${id}`, data),
  deleteContact: (id) => api.delete(`/client-portal/admin/contacts/${id}`),
  addClientAccess: (contactId, clientId) => api.post(`/client-portal/admin/contacts/${contactId}/add-client/${clientId}`),
  removeClientAccess: (contactId, clientId) => api.delete(`/client-portal/admin/contacts/${contactId}/remove-client/${clientId}`),
};

// K-PAX API
export const kpaxAPI = {
  // Status
  getStatus: () => api.get('/kpax/status', { _skipAuthRedirect: true }),
  // Devices
  listDevices: (params) => api.get('/kpax/devices', { params, _skipAuthRedirect: true }),
  getDeviceBySerial: (serialNumber) => api.get(`/kpax/device/${encodeURIComponent(serialNumber)}`, { _skipAuthRedirect: true }),
  getDeviceHistory: (serialNumber, params) => api.get(`/kpax/device/${encodeURIComponent(serialNumber)}/history`, { params, _skipAuthRedirect: true }),
  getDeviceAlerts: (serialNumber, params) => api.get(`/kpax/device/${encodeURIComponent(serialNumber)}/alerts`, { params, _skipAuthRedirect: true }),
  // Import
  importReadings: () => api.post('/integrations/kpax/import-readings'),
  // Sync statuses for ALL equipment from K-PAX API (includes incompatibles)
  syncStatuses: () => api.post('/kpax/sync-statuses'),
};

// HubSpot Deals API
export const hubspotAPI = {
  // Deals
  listDeals: (params) => api.get('/hubspot/deals', { params }),
  getDeal: (id) => api.get(`/hubspot/deals/${id}`),
  createContractFromDeal: (data) => api.post('/contracts/from-hubspot', data),
  importDeal: (dealId, clientId) => api.post(`/hubspot/import-deal/${dealId}`, null, { params: { client_id: clientId } }),
  syncContract: (contractId) => api.post(`/hubspot/sync/pull/${contractId}`),
  // Properties - for getting dropdown options
  getDealProperties: () => api.get('/hubspot/deal-properties'),
  getDealPropertyOptions: (propertyName) => api.get(`/hubspot/deal-property/${propertyName}/options`),
  getMotifFinContratOptions: () => api.get('/hubspot/motif-fin-contrat/options'),
  getOwners: () => api.get('/hubspot/owners'),
  // Contacts
  getContactsBySiret: (siret, search) => api.get(`/hubspot/contacts/by-siret/${siret}`, { params: { search } }),
  getContactsByCompanyId: (companyId) => api.get(`/hubspot/contacts/by-company/${companyId}`),
  getCompanyDataBySiret: (siret) => api.get(`/hubspot/company/by-siret/${siret}`),
  getClientHubspotData: (clientId, pipelineFilter) => api.get(`/clients/${clientId}/hubspot-data`, { 
    params: pipelineFilter ? { pipeline_filter: pipelineFilter } : {} 
  }),
  // Companies
  listCompanies: (params) => api.get('/hubspot-sync/companies', { params }),
  importCompany: (companyId) => api.post(`/hubspot/import-company/${companyId}`),
};

// HubSpot Config API
export const hubspotConfigAPI = {
  getTables: () => api.get('/hubspot/config/tables'),
  getFields: (table) => api.get(`/hubspot/config/fields/${table}`),
  getMappings: (table) => api.get(`/hubspot/config/mappings/${table}`),
  updateMappings: (table, mappings) => api.put(`/hubspot/config/mappings/${table}`, { mappings }),
  createCustomField: (data) => api.post('/hubspot/config/custom-fields', data),
  updateCustomField: (fieldId, data) => api.put(`/hubspot/config/custom-fields/${fieldId}`, data),
  deleteCustomField: (fieldId) => api.delete(`/hubspot/config/custom-fields/${fieldId}`),
  getCustomFields: (table) => api.get(`/hubspot/config/custom-fields/${table}`),
  getFieldTypes: () => api.get('/hubspot/config/field-types'),
  getLocalFieldOptions: (table, fieldName) => api.get(`/hubspot/config/field-options/${table}/${fieldName}`),
  getHubspotFieldOptions: (table, fieldName) => api.get(`/hubspot/config/hubspot-field-options/${table}/${fieldName}`),
  fetchHubspotFields: (table, search) => api.get(`/hubspot/config/hubspot-fields/${table}`, { params: { search } }),
  addHubspotField: (table, params) => api.post(`/hubspot/config/hubspot-fields/${table}/add`, null, { params }),
  // Gestion des propriétés (nouvelle interface)
  getProperties: (table) => api.get(`/hubspot/config/properties/${table}`),
  updateSystemField: (table, fieldName, data) => api.put(`/hubspot/config/system-field/${table}/${fieldName}`, data),
  updateFieldMapping: (table, fieldName, data) => api.put(`/hubspot/config/mapping/${table}/${fieldName}`, data),
  addFieldOption: (table, fieldName, option) => api.post(`/hubspot/config/field-option/${table}/${fieldName}`, { option }),
  deleteFieldOption: (table, fieldName, idx) => api.delete(`/hubspot/config/field-option/${table}/${fieldName}/${idx}`),
};

// Orders (Gestion des Commandes) API
export const ordersAPI = {
  list: (params) => api.get('/orders', { params }),
  get: (id) => api.get(`/orders/${id}`),
  create: (data) => api.post('/orders', data),
  update: (id, data) => api.patch(`/orders/${id}`, data),
  updateStage: (id, stage) => api.patch(`/orders/${id}/stage`, { stage }),
  sendEmail: (id) => api.post(`/orders/${id}/send-email`),
  sendPortalInvite: (id) => api.post(`/orders/${id}/send-portal-invite`),
  delete: (id) => api.delete(`/orders/${id}`),
  // Lot A — récap déplié d'une commande + export PDF d'une période
  recap: (id) => api.get(`/orders/${id}/recap`),
  exportPdf: (payload) => api.post('/orders/export-pdf', payload || {}, { responseType: 'blob' }),
  // KPI liste : données financières par commande (MRR, marge, coûts) à sommer côté client.
  financials: () => api.get('/orders/kpis/financials'),
};

// Document types (settings)
export const documentTypesAPI = {
  list: (params) => api.get('/document-types', { params }),
};

// Order documents — « Documents en attente » step
export const orderDocumentsAPI = {
  getRequest: (orderId) => api.get(`/orders/${orderId}/document-request`),
  setRequest: (orderId, data) => api.put(`/orders/${orderId}/document-request`, data),
  setSignataire: (orderId, data) => api.put(`/orders/${orderId}/document-request/signataire`, data),
  send: (orderId) => api.post(`/orders/${orderId}/document-request/send`),
  reset: (orderId) => api.post(`/orders/${orderId}/document-request/reset`),
  syncContact: (orderId) => api.post(`/orders/${orderId}/document-request/sync-contact`),
  // Étape 2 — analyse
  listDocuments: (orderId) => api.get(`/orders/${orderId}/documents`),
  getMotifs: (orderId) => api.get(`/orders/${orderId}/documents/motifs`),
  review: (orderId, documentId, data) => api.post(`/orders/${orderId}/documents/${documentId}/review`, data),
  resendRejected: (orderId, body) => api.post(`/orders/${orderId}/document-request/resend-rejected`, body || {}),
  // Étape « Demande de financement »
  getFinancement: (orderId) => api.get(`/orders/${orderId}/financement`),
  saveFinancement: (orderId, data) => api.put(`/orders/${orderId}/financement`, data),
  getContrat: (orderId) => api.get(`/orders/${orderId}/contrat`),
  saveContrat: (orderId, data) => api.put(`/orders/${orderId}/contrat`, data),
  setDocumentsSignesStatuts: (orderId, data) => api.put(`/orders/${orderId}/documents-signes/statuts`, data),
  // Facturation Pennylane depuis la commande (achat / LDWS / leaser) — crée un brouillon.
  // Optionnel : n'intervient pas dans la progression d'étape (gérée par statut_facture).
  generateInvoice: (orderId) => api.post(`/orders/${orderId}/generate-invoice`),
  // Client Pennylane : statut (lié ou non) + garantir (créer/lier + mandat SEPA).
  getPennylaneCustomer: (orderId) => api.get(`/orders/${orderId}/pennylane-customer`),
  ensurePennylaneCustomer: (orderId) => api.post(`/orders/${orderId}/ensure-pennylane-customer`),
  generateNumeroCommande: (orderId, payload) => api.post(`/orders/${orderId}/contrat/numero-commande`, payload || {}),
  getProductLines: (orderId) => api.get(`/orders/${orderId}/product-lines`),
  addProductLine: (orderId, data) => api.post(`/orders/${orderId}/product-lines`, data),
  updateProductLine: (orderId, lineId, data) => api.put(`/orders/${orderId}/product-lines/${lineId}`, data),
  deleteProductLine: (orderId, lineId) => api.delete(`/orders/${orderId}/product-lines/${lineId}`),
  duplicateProductLine: (orderId, lineId, withChildren = false) =>
    api.post(`/orders/${orderId}/product-lines/${lineId}/duplicate${withChildren ? '?with_children=true' : ''}`),
  syncProductLines: (orderId) => api.post(`/orders/${orderId}/product-lines/sync`),
  // Chantier B — coûts étape préparation (sync HubSpot bidirectionnelle)
  getCouts: (orderId) => api.get(`/orders/${orderId}/couts`),
  setCouts: (orderId, data) => api.put(`/orders/${orderId}/couts`, data),
  bonCommandePdf: (orderId) => api.get(`/orders/${orderId}/bon-commande/pdf`, { responseType: 'blob' }),
  bonCommandeSignatureState: (orderId) => api.get(`/orders/${orderId}/bon-commande/signature`),
  bonCommandeGenerateAndSign: (orderId, data) => api.post(`/orders/${orderId}/bon-commande/generate-and-sign`, data),
  bonCommandeSyncStatus: (orderId) => api.post(`/orders/${orderId}/bon-commande/sync-status`),
  bonCommandeSignedPdf: (orderId) => api.get(`/orders/${orderId}/bon-commande/signed-pdf`, { responseType: 'blob' }),
  sendFinancementEmail: (orderId, data) => api.post(`/orders/${orderId}/financement/email`, data),
  refuserDossier: (orderId, data) => api.post(`/orders/${orderId}/refuser`, data),
  rouvrirDossier: (orderId) => api.post(`/orders/${orderId}/rouvrir`),
  viewDocument: (clientId, docId) => api.get(`/clients/${clientId}/documents/${docId}/download`, { responseType: 'blob' }),
  deleteDocument: (clientId, docId) => api.delete(`/clients/${clientId}/documents/${docId}`),
  // Public (tokenized, no login)
  getBranding: () => api.get('/settings/public/branding', { _skipAuthRedirect: true }),
  getForm: (token) => api.get(`/public/order-form/${token}`, { _skipAuthRedirect: true }),
  // Must send multipart (matches the codebase's other file uploads) — otherwise the
  // axios instance default (application/json) is used and FastAPI can't parse the
  // form, silently dropping all files/fields.
  submitForm: (token, formData) =>
    api.post(`/public/order-form/${token}`, formData, {
      _skipAuthRedirect: true,
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

// Gestion des mandats SEPA
export const sepaMandatesAPI = {
  list: () => api.get('/sepa-mandates'),
  getRib: (clientId) => api.get(`/sepa-mandates/${clientId}/rib`),
  requestRib: (clientId) => api.post(`/sepa-mandates/${clientId}/request-rib`),
  cancelBankRequest: (clientId) => api.post(`/sepa-mandates/${clientId}/cancel-bank-request`),
  regenerateRum: (clientId) => api.post(`/sepa-mandates/${clientId}/regenerate-rum`),
  // Envoi en signature : PandaDoc (génération PDF + création + attente du brouillon
  // jusqu'à 60 s + envoi) dépasse le timeout par défaut (30 s) → 120 s dédiés,
  // sinon axios coupe et l'erreur s'affiche « Erreur lors de l'envoi » sans détail.
  sendMandateSignature: (clientId, data) =>
    api.post(`/sepa-mandates/${clientId}/send-mandate-signature`, data, { timeout: 120000 }),
  uploadManualMandate: (clientId, formData) =>
    api.post(`/sepa-mandates/${clientId}/manual-mandate`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  // Opérations de masse
  bulkRegenerateRum: (clientIds) => api.post('/sepa-mandates/bulk-regenerate-rum', { client_ids: clientIds }),
  bulkSendMandateSignature: (clientIds, mandateDate) =>
    api.post('/sepa-mandates/bulk-send-mandate-signature', {
      client_ids: clientIds, mandate_date: mandateDate || undefined,
    }, { timeout: 300000 }),  // jusqu'à 25 mandats × (PDF + PandaDoc ~60 s)
  // Recalcul du dernier rejet de prélèvement : avec clientIds → synchrone sur la
  // sélection ; sans → global en arrière-plan.
  recomputeRejections: (clientIds) =>
    api.post('/sepa-mandates/recompute-rejections',
      clientIds && clientIds.length ? { client_ids: clientIds } : {}),
  // Suivi de signature (PandaDoc)
  refreshMandateStatus: (clientId) =>
    api.post(`/sepa-mandates/${clientId}/refresh-mandate-status`, {}, { timeout: 30000 }),
  bulkRefreshStatus: (clientIds) =>
    api.post('/sepa-mandates/bulk-refresh-status',
      clientIds && clientIds.length ? { client_ids: clientIds } : {}, { timeout: 120000 }),
  mandateLink: (clientId) => api.get(`/sepa-mandates/${clientId}/mandate-link`),
  signedPdf: (clientId) => api.get(`/sepa-mandates/${clientId}/signed-pdf`, { responseType: 'blob' }),
  mandateHistory: (clientId) => api.get(`/sepa-mandates/${clientId}/history`),
  mandateHistoryPdf: (clientId, entryId) =>
    api.get(`/sepa-mandates/${clientId}/history/${entryId}/pdf`, { responseType: 'blob' }),
};

// Pennylane API
export const pennylaneAPI = {
  searchSupplierInvoices: (invoiceNumber) => api.get('/pennylane/supplier-invoices', { params: { invoice_number: invoiceNumber } }),
};

// Pennylane Historical Invoices API
export const pennylaneInvoicesAPI = {
  list: (params) => api.get('/pennylane-invoices', { params }),
  getStats: () => api.get('/pennylane-invoices/stats'),
  import: (data) => api.post('/pennylane-invoices/import', data),
  updateMapping: (invoiceId, equipmentId) => api.patch(`/pennylane-invoices/${invoiceId}/mapping`, { equipment_id: equipmentId }),
  getClientsWithPennylane: () => api.get('/pennylane-invoices/clients-with-pennylane'),
};

// Contacts API
export const contactsAPI = {
  list: (params) => api.get('/contacts', { params }),
  getByClient: (clientId) => api.get(`/clients/${clientId}/contacts`),
  get: (id) => api.get(`/contacts/${id}`),
  create: (data) => api.post('/contacts', data),
  update: (id, data) => api.put(`/contacts/${id}`, data),
  delete: (id) => api.delete(`/contacts/${id}`),
  syncFromHubspot: (data) => api.post('/contacts/sync-from-hubspot', data),
  syncAllFromHubspot: (clientId) => api.post(`/clients/${clientId}/sync-all-hubspot-contacts`),
};

// Sites API (Multi-site architecture)
export const sitesAPI = {
  list: (params) => api.get('/sites', { params }),
  getByClient: (clientId) => api.get(`/clients/${clientId}/sites`),
  get: (id) => api.get(`/sites/${id}`),
  create: (data) => api.post('/sites', data),
  update: (id, data) => api.put(`/sites/${id}`, data),
  delete: (id) => api.delete(`/sites/${id}`),
  // Geocoding endpoints
  geocodeSite: (id) => api.post(`/sites/${id}/geocode`),
  geocodeAll: () => api.post('/sites/geocode-all'),
  getWithoutCoordinates: () => api.get('/sites/without-coordinates'),
  createDefaultFromClient: (clientId) => api.post(`/clients/${clientId}/create-default-site`),
};

// Equipment Templates (Library) API
export const equipmentTemplatesAPI = {
  list: () => api.get('/equipment-templates'),
  create: (data) => api.post('/equipment-templates', data),
  update: (id, data) => api.put(`/equipment-templates/${id}`, data),
  delete: (id) => api.delete(`/equipment-templates/${id}`),
  importCSV: (data) => api.post('/equipment-templates/import-csv', data),
  // Equipment types management
  getTypes: () => api.get('/equipment-types'),
  migrateTypes: () => api.post('/equipment-types/migrate'),
  // Sub-categories (map type -> list of valid values)
  getSousCategories: () => api.get('/equipment-templates/sous-categories'),
  getSousCategoriesDetails: () => api.get('/equipment-templates/sous-categories/details'),
  addSousCategorie: (type_equipement, label) => api.post('/equipment-templates/sous-categories', { type_equipement, label }),
  deleteSousCategorie: (type_equipement, label) => api.delete('/equipment-templates/sous-categories', { params: { type_equipement, label } }),
  // Image management
  searchImages: (query, perPage = 6) => api.post('/equipment-templates/search-images', { query, per_page: perPage }),
  setImageFromUrl: (id, url) => api.post(`/equipment-templates/${id}/image-from-url`, { url }),
};

// Subscription Templates (Catalog) API
export const subscriptionTemplatesAPI = {
  list: () => api.get('/subscription-templates'),
  create: (data) => api.post('/subscription-templates', data),
  update: (id, data) => api.put(`/subscription-templates/${id}`, data),
  delete: (id) => api.delete(`/subscription-templates/${id}`),
  bulkAssign: (data) => api.post('/subscription-templates/bulk-assign', data),
  seedDefaults: () => api.post('/subscription-templates/seed-defaults'),
  // Photo de l'abonnement (bibliothèque) — reliée au catalogue, affichée sur les devis.
  uploadImage: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/subscription-templates/${id}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteImage: (id) => api.delete(`/subscription-templates/${id}/image`),
  imageUrl: (url) => (url ? (url.startsWith('data:') || url.startsWith('http') ? url : `${BACKEND_URL}${url}`) : null),
};

// Notifications API
export const notificationsAPI = {
  // Internal notifications (for support team)
  list: (params) => api.get('/notifications/internal', { params }),
  getUnreadCount: () => api.get('/notifications/internal/unread-count'),
  markAsRead: (id) => api.put(`/notifications/internal/${id}/read`),
  markAllAsRead: () => api.put('/notifications/internal/mark-all-read'),
  dismiss: (id) => api.put(`/notifications/internal/${id}/dismiss`),
  // Preferences
  getPreferences: () => api.get('/users/me/notification-preferences'),
  updatePreferences: (data) => api.put('/users/me/notification-preferences', data),
  // K-PAX monitoring
  checkDisconnectedAgents: (sendEmails = false) => 
    api.post('/kpax/check-disconnected', null, { params: { send_client_emails: sendEmails } }),
  // Test email
  sendTestEmail: (email) => api.post('/notifications/test-email', { email }),
};

// Equipment K-PAX status API
export const equipmentKpaxAPI = {
  updateStatus: (equipmentId, data) => api.put(`/equipments/${equipmentId}/kpax-status`, data),
};

// Scheduler API
export const schedulerAPI = {
  getJobs: () => api.get('/scheduler/jobs'),
  getLogs: (jobName, limit = 20) => api.get('/scheduler/logs', { params: { job_name: jobName, limit } }),
  runJob: (jobName) => api.post(`/scheduler/run/${jobName}`),
};

// Jobs Dashboard API
export const jobsDashboardAPI = {
  getDashboard: () => api.get('/admin/jobs-dashboard'),
};

// Queue API
export const queueAPI = {
  getStatus: () => api.get('/queue/status'),
  getJobs: (params) => api.get('/queue/jobs', { params }),
  getJob: (id) => api.get(`/queue/jobs/${id}`),
  getRateLimits: () => api.get('/queue/rate-limits'),
  retryFailed: (id) => api.post(`/queue/jobs/${id}/retry-failed`),
};

// Admin Logs API
export const adminLogsAPI = {
  getLogs: (params) => api.get('/admin/logs', { params }),
  getModules: () => api.get('/admin/logs/modules'),
};

// Tickets enhanced API
export const ticketsEnhancedAPI = {
  getKpaxTasks: (status) => api.get('/tickets/kpax-tasks', { params: { status } }),
  getStats: () => api.get('/tickets/stats'),
  updateCouts: (ticketId, couts) => api.put(`/tickets/${ticketId}/couts`, couts),
};

// Rentabilite API
export const rentabiliteAPI = {
  getClient: (clientId) => api.get(`/rentabilite/client/${clientId}`),
  getClientDashboard: (clientId, params) => api.get(`/rentabilite/client/${clientId}/dashboard`, { params }),
  getEquipment: (equipmentId) => api.get(`/rentabilite/equipment/${equipmentId}`),
  getGlobal: (params) => api.get('/rentabilite/global', { params }),
};

// Ticket Categories API
export const ticketCategoriesAPI = {
  list: (includeInactive = false) => api.get('/ticket-categories', { params: { include_inactive: includeInactive } }),
  create: (data) => api.post('/ticket-categories', data),
  update: (id, data) => api.put(`/ticket-categories/${id}`, data),
  delete: (id) => api.delete(`/ticket-categories/${id}`),
  seedDefaults: () => api.post('/ticket-categories/seed-defaults'),
  reorder: (orders) => api.put('/ticket-categories/reorder', { orders }),
};

// Order Checklist Templates API
export const orderChecklistTemplatesAPI = {
  list: (params) => api.get('/order-checklist-templates', { params }),
  create: (data) => api.post('/order-checklist-templates', data),
  update: (id, data) => api.put(`/order-checklist-templates/${id}`, data),
  delete: (id) => api.delete(`/order-checklist-templates/${id}`),
  seedDefaults: () => api.post('/order-checklist-templates/seed-defaults'),
};

export const orderChecklistAPI = {
  get: (orderId) => api.get(`/orders/${orderId}/checklist`),
  set: (orderId, items) => api.put(`/orders/${orderId}/checklist`, items),
  updateItem: (orderId, itemId, data) => api.patch(`/orders/${orderId}/checklist/${itemId}`, data),
};

// Travel Cost API
export const travelCostAPI = {
  calculate: (origin, destination, annualKm = 0) => 
    api.post('/travel-cost/calculate', null, { params: { origin, destination, annual_km: annualKm } }),
  estimate: (distanceKm) => 
    api.post('/travel-cost/estimate', null, { params: { distance_km: distanceKm } }),
  calculateForTicket: (ticketId) => api.post(`/tickets/${ticketId}/calculate-travel`),
  calculateForEquipment: (equipmentId) => api.post('/travel-cost/calculate-for-equipment', null, { params: { equipment_id: equipmentId } }),
};

// Indexation API
export const indexationAPI = {
  getSettings: () => api.get('/indexation/settings'),
  updateSettings: (data) => api.put('/indexation/settings', data),
  getDueContracts: (asOfDate = null) => api.get('/indexation/due', { params: { as_of_date: asOfDate } }),
  previewContract: (contractId) => api.get(`/indexation/preview/${contractId}`),
  applyToContract: (contractId) => api.post(`/indexation/apply/${contractId}`),
  applyBatch: (contractIds = null) => api.post('/indexation/apply-batch', { contract_ids: contractIds }),
  getHistory: (contractId) => api.get(`/indexation/history/${contractId}`),
  revert: (historyId) => api.post(`/indexation/revert/${historyId}`),
};

// Company Settings API
export const settingsAPI = {
  getInvoiceSettings: () => api.get('/settings/invoice'),
  updateInvoiceSettings: (data) => api.put('/settings/invoice', data),
  getPublicBranding: () => api.get('/settings/public/branding'),
  getTicketRouting: () => api.get('/settings/ticket-routing'),
  updateTicketRouting: (routing) => api.put('/settings/ticket-routing', { routing }),

  // Client portal ads library
  getAdsLibrary: () => api.get('/settings/ads/library'),
  uploadAdAsset: (slot, kind, file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post(`/settings/ads/upload/${slot}/${kind}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAdAsset: (slot, kind) => api.delete(`/settings/ads/${slot}/${kind}`),

  // Review-request brand config (logos from ads library + Google/Trustpilot + statements)
  getReviewsConfig: () => api.get('/settings/reviews/config'),
  updateReviewsConfig: (brands) => api.put('/settings/reviews/config', { brands }),
  previewReviewEmail: (slot) => api.get(`/settings/reviews/preview/${slot}`),
};

// Marques — kit de marque complet (logos origine/blanc/noir, image pub, couleurs, polices)
export const brandKitsAPI = {
  list: () => api.get('/brand-kits'),
  update: (brand, data) => api.put(`/brand-kits/${brand}`, data),
  uploadAsset: (brand, slot, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/brand-kits/${brand}/asset/${slot}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteAsset: (brand, slot) => api.delete(`/brand-kits/${brand}/asset/${slot}`),
  assetUrl: (url) => (url ? `${(process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '')}${url}` : ''),
};

// Review requests (send "leave a review" emails from the Clients page)
export const reviewsAPI = {
  resolveRecipients: (clientIds) => api.post('/reviews/recipients', { client_ids: clientIds }),
  send: (items) => api.post('/reviews/send', { items }, { timeout: 120000 }),
  list: (clientId) => api.get('/reviews/list', { params: clientId ? { client_id: clientId } : {} }),
  statsByClient: () => api.get('/reviews/stats-by-client'),
};

// PandaDoc API
export const pandadocAPI = {
  getStatus: () => api.get('/pandadoc/status'),
  listTemplates: () => api.get('/pandadoc/templates'),
  listDocuments: (status) => api.get('/pandadoc/documents', { params: { status } }),
  createIntervention: (data) => api.post('/pandadoc/intervention', data),
  sendDocument: (documentId, subject, message) => api.post(`/pandadoc/documents/${documentId}/send`, { subject, message }),
  getDocumentStatus: (documentId) => api.get(`/pandadoc/documents/${documentId}/status`),
  downloadDocument: (documentId) => api.get(`/pandadoc/documents/${documentId}/download`, { responseType: 'blob' }),
  // PDF Generation
  generatePDF: (data) => api.post('/pandadoc/generate-pdf', data, { responseType: 'blob' }),
  generateAndSign: (data) => api.post('/pandadoc/generate-and-sign', data),
  // Sync document status with ticket
  syncStatus: (ticketId) => api.post(`/pandadoc/sync-status/${ticketId}`),
  // Download signed PDF for a ticket
  downloadSignedPdf: (ticketId) => api.get(`/pandadoc/ticket/${ticketId}/signed-pdf`, { responseType: 'blob' }),
};

// Dropbox Sign API
export const dropboxSignAPI = {
  // Check configuration status
  getStatus: () => api.get('/dropbox-sign/status'),
  // Send document for signature
  sendForSignature: (data) => api.post('/dropbox-sign/send-for-signature', data),
  // Get signature status
  getSignatureStatus: (signatureRequestId) => api.get(`/dropbox-sign/signature-status/${signatureRequestId}`),
  // Download signed document
  downloadSigned: (signatureRequestId) => api.get(`/dropbox-sign/download-signed/${signatureRequestId}`, { responseType: 'blob' }),
};

// Services API (Service catalog & equipment services)
export const servicesAPI = {
  // Service catalog CRUD
  list: (params) => api.get('/services', { params }),
  get: (id) => api.get(`/services/${id}`),
  create: (data) => api.post('/services', data),
  update: (id, data) => api.put(`/services/${id}`, data),
  delete: (id) => api.delete(`/services/${id}`),
  seed: () => api.post('/services/seed'),
  
  // Equipment services (direct + via subscriptions)
  getEquipmentServices: (equipmentId) => api.get(`/equipments/${equipmentId}/services`),
  addToEquipment: (equipmentId, data) => api.post(`/equipments/${equipmentId}/services/add`, data),
  
  // Subscription services
  getSubscriptionServices: (subscriptionId) => api.get(`/subscriptions/${subscriptionId}/services`),
  addToSubscription: (subscriptionId, data) => api.post(`/subscriptions/${subscriptionId}/services`, data),
  updateSubscriptionService: (subServiceId, data) => api.put(`/subscription-services/${subServiceId}`, data),
  removeFromSubscription: (subServiceId) => api.delete(`/subscription-services/${subServiceId}`),
  cleanupDuplicates: () => api.post('/subscription-services/cleanup-duplicates'),
};

// Documentation API (restricted access)
export const documentationAPI = {
  checkAccess: () => api.get('/documentation/check-access'),
  getAll: () => api.get('/documentation'),
  getFile: (filename) => api.get(`/documentation/${filename}`),
  downloadPDF: () => api.get('/documentation/download/pdf', { responseType: 'blob' }),
  downloadSinglePDF: (filename) => api.get(`/documentation/download/pdf/${filename}`, { responseType: 'blob' }),
  // Dépôt e-Soleau (INPI)
  esoleauBuild: () => api.post('/documentation/esoleau/build'),
  esoleauStatus: () => api.get('/documentation/esoleau/status'),
  esoleauDownload: (name) => api.get('/documentation/esoleau/download', { params: { name }, responseType: 'blob' }),
};

// Connexion Gmail PERSONNELLE (par utilisateur) — pour le temps e-mail du justificatif
export const gmailPersonalAPI = {
  authorize: () => api.get('/oauth/gmail/me/authorize'),
  status: () => api.get('/oauth/gmail/me/status'),
  disconnect: () => api.delete('/oauth/gmail/me/disconnect'),
};

// Permissions API
export const permissionsAPI = {
  getCategories: () => api.get('/permissions/categories'),
  getAllPermissions: () => api.get('/permissions/all'),
  getRoles: () => api.get('/permissions/roles'),
  getRolePermissions: (role) => api.get(`/permissions/role/${role}`),
  getUserPermissions: (userId) => api.get(`/permissions/user/${userId}`),
  updateUserPermissions: (userId, data) => api.put(`/permissions/user/${userId}`, data),
  toggleUserPermission: (userId, permissionCode, enabled) => 
    api.post(`/permissions/user/${userId}/toggle?permission_code=${permissionCode}&enabled=${enabled}`),
  resetUserPermissions: (userId) => api.post(`/permissions/user/${userId}/reset`),
  getMatrix: () => api.get('/permissions/matrix'),
};

// Contract Tracking API (Suivi Contrat)
export const contractTrackingAPI = {
  // Renewals (Tacit Reconductions)
  getRenewals: (params) => api.get('/contract-tracking/renewals', { params }),
  getRenewal: (contractId) => api.get(`/contract-tracking/renewals/${contractId}`),
  updateRenewal: (contractId, data) => api.put(`/contract-tracking/renewals/${contractId}`, data),
  sendRenewalEmail: (contractId, emailType) => api.post(`/contract-tracking/renewals/${contractId}/send-email?email_type=${emailType}`),
  sendBulkRenewalEmails: (alertDays) => api.post(`/contract-tracking/renewals/send-bulk?alert_days=${alertDays}`),
  
  // Indexations
  getIndexations: (params) => api.get('/contract-tracking/indexations', { params }),
  applyIndexation: (contractId, rate, newRent) => api.post(`/contract-tracking/indexations/${contractId}/apply?rate=${rate}&new_rent=${newRent}`),
  previewIndexation: (contractId, rate) => api.get(`/contract-tracking/indexations/${contractId}/preview`, { params: { rate } }),
  revertIndexation: (contractId) => api.post(`/contract-tracking/indexations/${contractId}/revert`),
  sendIndexationEmail: (contractId) => api.post(`/contract-tracking/indexations/${contractId}/send-email`),
  sendBulkIndexationEmails: (month) => api.post(`/contract-tracking/indexations/send-bulk?month=${month}`),
  bulkApplyIndexations: (items) => api.post('/contract-tracking/indexations/bulk-apply', { items }),
  bulkRevertIndexations: (contractIds) => api.post('/contract-tracking/indexations/bulk-revert', { contract_ids: contractIds }),
  getAvailableYears: () => api.get('/contract-tracking/indexations/years'),
  getDashboard: (params = {}) => api.get('/contract-tracking/dashboard', { params }),
  getDashboardPdf: (params = {}) => api.get('/contract-tracking/dashboard/pdf', { params, responseType: 'blob', timeout: 120000 }),
  // Pertes historiques (source HubSpot « Motif fin ») — cache nocturne
  getHistoricalLosses: (refresh = false) => api.get('/contract-tracking/historical-losses', { params: refresh ? { refresh: true } : {}, timeout: 60000 }),
  getHistoricalLossesPdf: (params = {}) => api.get('/contract-tracking/historical-losses/pdf', { params, responseType: 'blob', timeout: 120000 }),
};

// Contract Terminations API (Résiliations)
export const contractTerminationsAPI = {
  // Suivi des résiliations
  getTerminations: (params) => api.get('/contract-terminations/terminations', { params }),
  getAvailableYears: () => api.get('/contract-terminations/years'),
  
  // Indemnité de résiliation - Contracts list
  listContractsForIndemnity: (params) => api.get('/contract-terminations/indemnity/contracts', { params }),
  
  // Indemnité de résiliation - Details
  getIndemnityDetails: (contractId) => api.get(`/contract-terminations/indemnity/${contractId}`),
  calculateIndemnity: (contractId, data) => api.post(`/contract-terminations/indemnity/${contractId}/calculate`, data),
  updateIndemnity: (contractId, data) => api.put(`/contract-terminations/indemnity/${contractId}`, data),
  sendIndemnityEmail: (contractId) => api.post(`/contract-terminations/indemnity/${contractId}/send-email`),
  
  // P2 FIX: Add cancel endpoint
  cancelIndemnity: (contractId, params) => api.post(`/contract-terminations/indemnity/${contractId}/cancel`, null, { params }),
  
  // Simulation
  simulateIndemnity: (contractId, data) => api.post(`/contract-terminations/indemnity/${contractId}/simulate`, data),
  sendSimulationEmail: (contractId, data) => api.post(`/contract-terminations/indemnity/${contractId}/send-simulation`, data),
  
  // Settings
  getSettings: () => api.get('/contract-terminations/settings'),
  updateSettings: (params) => api.put('/contract-terminations/settings', null, { params }),
};

// Contract Letters API (Lettres de résiliation)
// Email tracking API (Resend webhook events + email logs)
export const emailTrackingAPI = {
  getLogs: (params) => api.get('/emails/logs', { params }),
  getLog: (id) => api.get(`/emails/logs/${id}`),
  getEvents: (emailId) => api.get(`/email-events/${emailId}`),
  getRecipientEvents: (email, limit = 50) => api.get(`/email-events/recipient/${encodeURIComponent(email)}`, { params: { limit } }),
};

export const contractLettersAPI = {
  getTemplates: () => api.get('/contract-letters/templates'),
  generateLetter: (data) => api.post('/contract-letters/generate', data),
  downloadPdf: (filename) => api.get(`/contract-letters/download/${encodeURIComponent(filename)}`, { responseType: 'blob' }),
  getDownloadUrl: (filename) => {
    const token = localStorage.getItem('token') || '';
    return `${process.env.REACT_APP_BACKEND_URL}/api/contract-letters/download-public/${encodeURIComponent(filename)}?token=${token}`;
  },
  getHistory: (contractId) => api.get(`/contract-letters/history/${contractId}`),
  getClientHistory: (clientId) => api.get(`/contract-letters/history/client/${clientId}`),
  deleteHistory: (historyId) => api.delete(`/contract-letters/history/${historyId}`),
  sendTaciteReconduction: (contractId, contactId, duree = '12 mois') =>
    api.post(`/contract-letters/renewal/${contractId}/send-tacite-reconduction`, null, {
      params: { contact_id: contactId, duree_reconduction: duree }
    }),
  sendResiliationEcheance: (contractId, contactId) =>
    api.post(`/contract-letters/renewal/${contractId}/send-resiliation-echeance`, null, {
      params: { contact_id: contactId }
    }),
  sendResiliationAnticipee: (contractId, contactId, fraisRetour = 490, nbEquip = 0) =>
    api.post(`/contract-letters/indemnity/${contractId}/send-resiliation-anticipee`, null, {
      params: { contact_id: contactId, frais_retour_unitaire: fraisRetour, nb_equipements: nbEquip }
    }),
  sendMiseEnDemeure: (contractId, contactId, montantDu) =>
    api.post(`/contract-letters/indemnity/${contractId}/send-mise-en-demeure`, null, {
      params: { contact_id: contactId, montant_total_du: montantDu }
    }),
  sendDepositRestitution: (contractId, contactId, fraisRetour = 0, nbEquip = 0, impayees = 0, autres = 0) =>
    api.post(`/contract-letters/deposit/${contractId}/send-restitution`, null, {
      params: {
        contact_id: contactId,
        frais_retour_unitaire: fraisRetour,
        nb_equipements: nbEquip,
        factures_impayees: impayees,
        autres_frais: autres
      }
    }),
};

// SEPA API
export const sepaAPI = {
  getBankAccounts: () => api.get('/sepa/bank-accounts'),
  getCustomers: (page = 1, perPage = 50) => api.get('/sepa/customers', { params: { page, per_page: perPage } }),
  getBalances: (ledgerAccountIds, startDate = null) =>
    api.post('/sepa/balances', { ledger_account_ids: ledgerAccountIds, start_date: startDate }),
  clearCache: () => api.post('/sepa/cache/clear'),
  exportXml: (request) => api.post('/sepa/export/xml', request, { responseType: 'blob' }),
  exportXlsx: (request) => api.post('/sepa/export/xlsx', request, { responseType: 'blob' }),
};

// ETL Logs API
export const etlAPI = {
  getLogs: (params) => api.get('/etl/logs', { params }),
  getStats: () => api.get('/etl/stats'),
  getStatsBySource: () => api.get('/etl/stats/by-source'),
};

// Vitrine : demandes de devis déposées par le site public
export const storefrontLeadsAPI = {
  list: (params) => api.get('/storefront/leads', { params }),
  get: (id) => api.get(`/storefront/leads/${id}`),
  update: (id, data) => api.patch(`/storefront/leads/${id}`, data),
};

// Vitrine : articles de blog du site public (écriture réservée aux droits storefront:write / publish / redirects)
export const storefrontArticlesAPI = {
  list: (params) => api.get('/storefront/articles', { params }),
  categories: () => api.get('/storefront/articles/categories'),
  get: (id) => api.get(`/storefront/articles/${id}`),
  create: (data) => api.post('/storefront/articles', data),
  update: (id, data) => api.patch(`/storefront/articles/${id}`, data),
  publish: (id, revision) => api.post(`/storefront/articles/${id}/publish`, revision === undefined ? {} : { revision }),
  unpublish: (id, revision) => api.post(`/storefront/articles/${id}/unpublish`, revision === undefined ? {} : { revision }),
  archive: (id, revision) => api.post(`/storefront/articles/${id}/archive`, revision === undefined ? {} : { revision }),
  remove: (id) => api.delete(`/storefront/articles/${id}`),
};

// Vitrine : catégories et marques du site public (même forme que les articles ; `parents` : choix du parent d'une catégorie)
const storefrontTaxonomyAPI = (base) => ({
  list: (params) => api.get(base, { params }),
  get: (id) => api.get(`${base}/${id}`),
  create: (data) => api.post(base, data),
  update: (id, data) => api.patch(`${base}/${id}`, data),
  publish: (id, revision) => api.post(`${base}/${id}/publish`, revision === undefined ? {} : { revision }),
  unpublish: (id, revision) => api.post(`${base}/${id}/unpublish`, revision === undefined ? {} : { revision }),
  archive: (id, revision) => api.post(`${base}/${id}/archive`, revision === undefined ? {} : { revision }),
  remove: (id) => api.delete(`${base}/${id}`),
});
export const storefrontCategoriesAPI = { ...storefrontTaxonomyAPI('/storefront/categories'), parents: () => api.get('/storefront/categories/parents') };
export const storefrontBrandsAPI = storefrontTaxonomyAPI('/storefront/brands');
// Vitrine : fiches produit du site public (même forme que les catégories et les marques)
export const storefrontProductsAPI = storefrontTaxonomyAPI('/storefront/products');

// Vitrine : définitions des caractéristiques. Lecture `storefront:read` (liste filtrable, détail, groupes, aperçu d'impact, journal) ;
// modifier, actions groupées, mise en avant par catégorie et annulation `storefront:write` ; supprimer `storefront:publish`.
export const storefrontFeaturesAPI = {
  list: (params) => api.get('/storefront/features', { params }),
  get: (id, params) => api.get(`/storefront/features/${id}`, { params }),
  groups: () => api.get('/storefront/feature-groups'),
  impact: (id, params) => api.get(`/storefront/features/${id}/impact`, { params }),
  update: (id, data) => api.patch(`/storefront/features/${id}`, data),
  bulk: (data) => api.post('/storefront/features/bulk', data),
  bulkDelete: (data) => api.post('/storefront/features/bulk-delete', data),
  remove: (id) => api.delete(`/storefront/features/${id}`),
  duplicates: () => api.get('/storefront/features/duplicates'),
  mergePreview: (data) => api.post('/storefront/features/merge/preview', data),
  merge: (data) => api.post('/storefront/features/merge', data),
  categoryHighlights: (categoryId) => api.get(`/storefront/categories/${categoryId}/highlights`),
  setCategoryHighlights: (categoryId, data) => api.put(`/storefront/categories/${categoryId}/highlights`, data),
  highlight: (id, data) => api.post(`/storefront/features/${id}/highlight`, data),
  unhighlight: (id, categoryId, params) => api.delete(`/storefront/features/${id}/highlight/${categoryId}`, { params }),
  changelog: (params) => api.get('/storefront/changelog', { params }),
  undo: (entryId) => api.post(`/storefront/changelog/${entryId}/undo`),
};

// Vitrine : groupes de caractéristiques (« Écran », « Mémoire »…). Lecture storefront:read ; créer, renommer, ordonner storefront:write ;
// fusionner et supprimer storefront:publish. L'annulation passe par le journal commun (storefrontFeaturesAPI.undo).
export const storefrontGroupsAPI = {
  list: () => api.get('/storefront/feature-groups'),
  get: (id, params) => api.get(`/storefront/feature-groups/${id}`, { params }),
  impact: (id, params) => api.get(`/storefront/feature-groups/${id}/impact`, { params }),
  create: (data) => api.post('/storefront/feature-groups', data),
  update: (id, data) => api.patch(`/storefront/feature-groups/${id}`, data),
  reorder: (data) => api.put('/storefront/feature-groups/order', data),
  remove: (id) => api.delete(`/storefront/feature-groups/${id}`),
  mergePreview: (data) => api.post('/storefront/feature-groups/merge/preview', data),
  merge: (data) => api.post('/storefront/feature-groups/merge', data),
};

// Vitrine : gammes de produits (« Ecosys », « LaserJet »…). Lecture storefront:read ; créer, renommer, retirer / ajouter / déplacer des fiches
// storefront:write ; fusionner et supprimer storefront:publish. L'annulation passe par le journal commun (storefrontFeaturesAPI.undo).
export const storefrontFamiliesAPI = {
  list: (params) => api.get('/storefront/families', { params }),
  get: (id, params) => api.get(`/storefront/families/${id}`, { params }),
  impact: (id, params) => api.get(`/storefront/families/${id}/impact`, { params }),
  searchProducts: (id, q) => api.get(`/storefront/families/${id}/product-search`, { params: { q } }),
  create: (data) => api.post('/storefront/families', data),
  update: (id, data) => api.patch(`/storefront/families/${id}`, data),
  addProducts: (id, data) => api.post(`/storefront/families/${id}/products`, data),
  removeProducts: (id, data) => api.post(`/storefront/families/${id}/products/remove`, data),
  removeProduct: (id, productId) => api.delete(`/storefront/families/${id}/products/${productId}`),
  remove: (id) => api.delete(`/storefront/families/${id}`),
  mergePreview: (data) => api.post('/storefront/families/merge/preview', data),
  merge: (data) => api.post('/storefront/families/merge', data),
};

// Vitrine : garanties, la liste de choix de l'éditeur de fiche (« 1 an », « 3 ans »…). Lecture storefront:read ; créer, renommer, activer / désactiver,
// réordonner storefront:write ; supprimer (refusé tant qu'une fiche l'utilise) storefront:publish. L'annulation passe par le journal commun.
export const storefrontWarrantiesAPI = {
  list: (params) => api.get('/storefront/warranties', { params }),
  impact: (id, params) => api.get(`/storefront/warranties/${id}/impact`, { params }),
  create: (data) => api.post('/storefront/warranties', data),
  update: (id, data) => api.patch(`/storefront/warranties/${id}`, data),
  reorder: (data) => api.put('/storefront/warranties/order', data),
  remove: (id) => api.delete(`/storefront/warranties/${id}`),
};

// Vitrine : redirections et pages retirées (registre des adresses). Tout exige storefront:redirects.
export const storefrontRedirectsAPI = {
  list: (params) => api.get('/storefront/redirects', { params }),
  check: (path) => api.get('/storefront/redirects/check', { params: { path } }),
  create: (data) => api.post('/storefront/redirects', data),
  remove: (path) => api.delete('/storefront/redirects', { params: { path } }),
};

// Vitrine : médiathèque (images téléversées). Lecture storefront:read ; téléverser, modifier, retirer storefront:media.
export const storefrontMediaAPI = {
  list: (params) => api.get('/storefront/media', { params }),
  get: (id) => api.get(`/storefront/media/${id}`),
  upload: (file, alt) => {
    const formData = new FormData();
    formData.append('file', file);
    if (alt) formData.append('alt', alt);
    return api.post('/storefront/media', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  update: (id, data) => api.patch(`/storefront/media/${id}`, data),
  remove: (id) => api.delete(`/storefront/media/${id}`),
};

// LMS Formation API
export const lmsAPI = {
  getModules: () => api.get('/lms/modules'),
  getModuleSections: (moduleId) => api.get(`/lms/modules/${moduleId}/sections`),
  getSection: (sectionId) => api.get(`/lms/sections/${sectionId}`),
  markRead: (sectionId) => api.post(`/lms/sections/${sectionId}/mark-read`),
  markUnread: (sectionId) => api.post(`/lms/sections/${sectionId}/mark-unread`),
  submitExercise: (sectionId, reponse) => api.post(`/lms/sections/${sectionId}/exercise`, { reponse }),
  seed: () => api.post('/lms/admin/seed'),
  deleteModule: (moduleId) => api.delete(`/lms/modules/${moduleId}`),
};

// Commercial API
// Flux d'activité (admin) — deux sources cloisonnées.
export const activityAPI = {
  getNeuroops: (params) => api.get('/activity/neuroops', { params, timeout: 60000 }),
  getHubspot: (params) => api.get('/activity/hubspot', { params, timeout: 120000 }),
  getFilterOptions: () => api.get('/commercial/dashboard/filters'),
  precompute: () => api.post('/activity/precompute'),
  heartbeat: (path) => api.post('/activity/heartbeat', { path }, { timeout: 8000 }),
  // Flush final (onglet masqué/fermé) : fetch `keepalive` pour survivre au
  // déchargement de la page, avec le Bearer (sendBeacon ne pose pas d'en-tête auth).
  heartbeatFlush: (path) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      fetch(`${API_BASE}/activity/heartbeat`, {
        method: 'POST', keepalive: true,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ path: path || '' }),
      }).catch(() => {});
    } catch (e) { /* best-effort */ }
  },
  downloadNeuroopsPdf: (params) => api.get('/activity/neuroops/pdf', { params, responseType: 'blob', timeout: 60000 }),
  downloadHubspotPdf: (params) => api.get('/activity/hubspot/pdf', { params, responseType: 'blob', timeout: 90000 }),
  downloadReportPdf: (params) => api.get('/activity/report/pdf', { params, responseType: 'blob', timeout: 90000 }),
  // Rapport d'activité CERTIFIABLE par utilisateur (données officielles HubSpot + SHA-256).
  getUserReport: (params) => api.get('/activity/report', { params, timeout: 120000 }),
  downloadUserReportPdf: (params) => api.get('/activity/report/user-pdf', { params, responseType: 'blob', timeout: 120000 }),
  // Mode « constat » : journal des étapes + empreinte ; archive horodatée (ZIP).
  getReportConstat: (params) => api.get('/activity/report/constat', { params, timeout: 120000 }),
  downloadReportArchive: (params) => api.get('/activity/report/archive', { params, responseType: 'blob', timeout: 180000 }),
  // Sources externes (désactivées par défaut) : relevé d'appels + export Workspace.
  uploadCallLog: (params, fileObj) => { const fd = new FormData(); fd.append('file', fileObj); return api.post('/activity/call-log', fd, { params, timeout: 180000, headers: { 'Content-Type': 'multipart/form-data' } }); },
  uploadWorkspace: (params, fileObj) => { const fd = new FormData(); fd.append('file', fileObj); return api.post('/activity/workspace', fd, { params, timeout: 180000, headers: { 'Content-Type': 'multipart/form-data' } }); },
  externalEnable: (params) => api.post('/activity/external-enable', null, { params, timeout: 30000 }),
  // Valeurs de contrôle (§D) : comparaison + filigrane PDF « NON CONSTATABLE ».
  getControlValues: (params) => api.get('/activity/control-values', { params, timeout: 30000 }),
  setControlValues: (params, expected) => api.post('/activity/control-values', expected, { params, timeout: 30000 }),
  // Journal d'audit (import CSV officiel « user-audit-log ») : heures/connexions.
  getAuditStatus: (params) => api.get('/activity/audit-csv', { params, timeout: 30000 }),
  uploadAuditCsv: (params, fileObj) => {
    const fd = new FormData();
    fd.append('file', fileObj);
    return api.post('/activity/audit-csv', fd, { params, timeout: 180000, headers: { 'Content-Type': 'multipart/form-data' } });
  },
};

export const commercialAPI = {
  getDashboard: () => api.get('/commercial/dashboard'),
  // Nouveau tableau de bord commercial (filtres période/propriétaire/type/source/mode).
  getAnalytics: (params) => api.get('/commercial/dashboard/analytics', { params }),
  getFilterOptions: () => api.get('/commercial/dashboard/filters'),
  // Comparer deux propriétaires (KPIs côte à côte + note d'expert).
  compareOwners: (params) => api.get('/commercial/dashboard/compare', { params }),
  downloadAnalyticsPdf: (params) => api.get('/commercial/dashboard/analytics/pdf', { params, responseType: 'blob' }),
  // Le backfill enchaîne jusqu'à `limit` appels HubSpot séquentiels ; on désactive
  // le timeout Axios global (30s) pour ce lot et on le borne pour éviter un batch
  // trop gros par clic (des clics successifs poursuivent le backfill côté serveur).
  backfillHubspotFields: (limit = 300) =>
    api.post('/commercial/dashboard/backfill-hubspot-fields', null, {
      params: { limit },
      timeout: 0,
    }),
  getClients: () => api.get('/commercial/clients'),
  getClient: (id) => api.get(`/commercial/clients/${id}`),
  getRenewals: () => api.get('/commercial/renewals'),
  assignCommercial: (clientId, commercialId) => api.put(`/commercial/assign/${clientId}`, { commercial_id: commercialId }),
  getSectors: () => api.get('/commercial/sectors'),
  setSector: (commercialId, secteur) => api.put(`/commercial/sectors/${commercialId}`, { secteur }),
  reassignBySector: (body) => api.post('/commercial/sectors/reassign', body || {}),
};

// Tableau de bord PROSPECTION (haut du tunnel : leads/devis). Même système de
// filtres, de présentation et de récupération de données que le dashboard
// commercial, braqué sur les prospects croisés avec le module devis.
// Télécom — CDR (consommation). Import manuel, synchro FTP, agrégation par client.
export const telecomAPI = {
  importCdr: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/telecom/cdr/import', fd, {
      headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000,
    });
  },
  syncFtp: (full = false) => api.post('/telecom/cdr/sync-ftp', null, { params: full ? { full: true } : {}, timeout: 180000 }),
  periods: () => api.get('/telecom/cdr/periods'),
  summary: (period) => api.get('/telecom/cdr/summary', { params: period ? { period } : {}, timeout: 120000 }),
  getFtpConfig: () => api.get('/telecom/ftp-config'),
  setFtpConfig: (data) => api.put('/telecom/ftp-config', data),
  mappingClients: () => api.get('/telecom/mapping/clients'),
  heliosClients: () => api.get('/telecom/mapping/helios-clients'),
  setClientMapping: (data) => api.put('/telecom/mapping/clients', data),
  grids: (type) => api.get('/telecom/grilles', { params: type ? { type } : {} }),
  importGrid: (formData) => api.post('/telecom/grilles/import', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000,
  }),
  importGridsBulk: (formData) => api.post('/telecom/grilles/import-bulk', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000,
  }),
  reloadGrid: (id, formData) => api.post(`/telecom/grilles/${id}/reload`, formData, {
    headers: { 'Content-Type': 'multipart/form-data' }, timeout: 120000,
  }),
  getGrid: (id) => api.get(`/telecom/grilles/${id}`),
  gridDestinations: () => api.get('/telecom/grille-destinations'),
  editGridLines: (id, edits) => api.put(`/telecom/grilles/${id}/lines`, { edits }),
  updateGrid: (id, data) => api.put(`/telecom/grilles/${id}`, data),
  deleteGrid: (id) => api.delete(`/telecom/grilles/${id}`),
  abonnementsBibliotheque: () => api.get('/telecom/abonnements/bibliotheque'),
  getAssociation: (scope, refId) => api.get('/telecom/associations', { params: { scope, ref_id: refId } }),
  setAssociation: (data) => api.put('/telecom/associations', data),
  listAssociations: (scope) => api.get('/telecom/associations-list', { params: { scope } }),
  bulkAssociate: (data) => api.put('/telecom/associations-bulk', data),
  pricingFacturation: ({ period, increment } = {}) => api.get('/telecom/pricing/facturation', {
    params: { ...(period ? { period } : {}), ...(increment != null ? { increment } : {}) },
    timeout: 120000,
  }),
  pricingSimulate: ({ period, gridId, type, increment } = {}) => api.get('/telecom/pricing/simulate', {
    params: {
      ...(period ? { period } : {}),
      ...(gridId ? { grid_id: gridId } : {}),
      ...(type ? { type } : {}),
      ...(increment != null ? { increment } : {}),
    },
    timeout: 120000,
  }),
};

export const prospectionAPI = {
  getAnalytics: (params) => api.get('/prospection/dashboard/analytics', { params }),
  // Activité d'engagement : lecture live HubSpot → chargée à part, timeout élargi.
  getActivite: (params) => api.get('/prospection/dashboard/activite', { params, timeout: 120000 }),
  compare: (params) => api.get('/prospection/dashboard/compare', { params }),
  getFilterOptions: () => api.get('/prospection/dashboard/filters'),
  downloadAnalyticsPdf: (params) => api.get('/prospection/dashboard/analytics/pdf', { params, responseType: 'blob', timeout: 0 }),
  // Rafraîchit les marges catalogue des devis du périmètre (même principe que le
  // rafraîchissement HubSpot du dashboard commercial) — lot potentiellement long.
  refresh: () => api.post('/prospection/dashboard/refresh', null, { timeout: 0 }),
};

// Analyse de l'activité commerciale (lecture live HubSpot).
export const commercialActivityAPI = {
  // Lecture live HubSpot : peut être longue sur un gros portail → timeout élargi.
  getAnalytics: (params) => api.get('/commercial-activity/analytics', { params, timeout: 120000 }),
  createTasks: (body) => api.post('/commercial-activity/tasks', body || {}, { timeout: 120000 }),
  exportPdf: (params) => api.get('/commercial-activity/export.pdf', { params, responseType: 'blob', timeout: 120000 }),
  refreshCache: () => api.post('/commercial-activity/refresh-cache', {}, { timeout: 180000 }),
};

// Recovery (Recouvrement) API
export const recoveryAPI = {
  getOverdueInvoices: (params) => api.get('/recovery/overdue-invoices', { params }),
  getDashboard: () => api.get('/recovery/dashboard'),
  getCustomerDetail: (customerId) => api.get(`/recovery/customer/${customerId}`),
  getCustomerLedgerBalance: (clientId) => api.get(`/recovery/ledger/customer/${clientId}/balance`),
  getCustomerContacts: (customerId) => api.get(`/recovery/customer/${customerId}/contacts`),
  getActions: (customerId) => api.get(`/recovery/actions/${customerId}`),
  createAction: (data) => api.post('/recovery/actions', data),
  generateTemplate: (customerId, templateType) =>
    api.post(`/recovery/template/generate/${customerId}`, null, { params: { template_type: templateType } }),
  sendTemplateEmail: (customerId, params) =>
    api.post(`/recovery/template/send-email/${customerId}`, null, { params }),
  sendCorrespondence: (customerId, params) =>
    api.post(`/recovery/ai/send-correspondence/${customerId}`, null, { params }),
  executeAllPending: () => api.post('/recovery/sequence/execute-all-pending'),
  bulkCreateSequences: (data) => api.post('/recovery/sequence/bulk-create', data),
  bulkSuspend: (data) => api.post('/recovery/bulk-suspend', data),
  syncLedger: (fullSync = false) => api.post('/recovery/ledger/sync', null, { params: { full_sync: fullSync } }),
  getSyncProgress: () => api.get('/recovery/ledger/sync-progress'),
};

export const catalogueAPI = {
  list: (params) => api.get('/hubspot-products', { params }),
  stats: () => api.get('/hubspot-products/stats'),
  create: (data) => api.post('/hubspot-products', data),
  sync: (ids) => api.post('/hubspot-products/sync', ids ? { ids } : {}, { timeout: 180000 }),
  pushToHubspot: (ids) => api.post('/hubspot-products/push-to-hubspot', ids ? { ids } : {}, { timeout: 180000 }),
  get: (id) => api.get(`/hubspot-products/${id}`),
  update: (id, data) => api.put(`/hubspot-products/${id}`, data),
  getSuggestions: (id, target) => api.get(`/hubspot-products/${id}/suggestions`, { params: { target } }),
  reconcile: (id, data) => api.post(`/hubspot-products/${id}/reconcile`, data),
  createFrom: (id, target) => api.post(`/hubspot-products/${id}/create-from`, { target }),
  deleteProduct: (id) => api.delete(`/hubspot-products/${id}`),
  bulkUpdate: (ids, updates) => api.post('/hubspot-products/bulk-update', { ids, updates }),
  bulkTransfer: (ids, source_field, dest_field) => api.post('/hubspot-products/bulk-transfer', { ids, source_field, dest_field }),
  bulkDelete: (ids) => api.post('/hubspot-products/bulk-delete', { ids }),
  syncImages: (ids) => api.post('/hubspot-products/sync-images', ids ? { ids } : {}, { timeout: 180000 }),
  // Photo du produit (catalogue) — même principe qu'équipements/abonnements.
  uploadImage: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/hubspot-products/${id}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteImage: (id) => api.delete(`/hubspot-products/${id}/image`),
  imageUrl: (url) => (url ? (url.startsWith('data:') || url.startsWith('http') ? url : `${BACKEND_URL}${url}`) : null),
  // Transfert de photo entre un produit et l'entité reliée (équipement/abonnement),
  // dans le sens choisi. Fonctionne sur une ou plusieurs lignes sélectionnées.
  bulkImageTransfer: (ids, { target, direction, overwrite = true }) =>
    api.post('/hubspot-products/bulk-image-transfer', { ids, target, direction, overwrite }),
};

export const comparativeStudiesAPI = {
  list: (params) => api.get('/comparative-studies', { params }),
  create: (data) => api.post('/comparative-studies', data),
  get: (id) => api.get(`/comparative-studies/${id}`),
  update: (id, data) => api.put(`/comparative-studies/${id}`, data),
  delete: (id) => api.delete(`/comparative-studies/${id}`),
  prefill: (clientId) => api.get(`/comparative-studies/prefill/${clientId}`),
  // Préremplissage prospect depuis un deal HubSpot (rattachement transaction,
  // même logique que le devis).
  hubspotDealPrefill: (dealId) => api.get(`/comparative-studies/hubspot-deal/${dealId}/prefill`),
  exportPdf: (id, mode = 'summary') => api.get(`/comparative-studies/${id}/export/pdf`, { params: { mode }, responseType: 'blob' }),
};

export const devisAPI = {
  list: (params) => api.get('/devis', { params }),
  create: (data) => api.post('/devis', data),
  get: (id) => api.get(`/devis/${id}`),
  update: (id, data) => api.put(`/devis/${id}`, data),
  delete: (id) => api.delete(`/devis/${id}`),
  duplicate: (id) => api.post(`/devis/${id}/dupliquer`),
  send: (id, data) => api.post(`/devis/${id}/envoyer`, data || {}),
  // Brouillon d'e-mail prérempli (objet + corps contextuels + signature) pour la modale d'envoi.
  emailPreview: (id) => api.get(`/devis/${id}/email-preview`),
  // Contacts associés au deal + entreprise (destinataire / copie).
  dealContacts: (id) => api.get(`/devis/${id}/deal-contacts`),
  // Réactualise la transaction HubSpot (société / contact / source) et renvoie les contacts.
  refreshDeal: (id) => api.post(`/devis/${id}/refresh-deal`),
  // Propriétaire du deal (changement depuis Hélios → HubSpot deal + entreprise + contacts).
  assignableOwners: () => api.get('/devis/hubspot/assignable-owners'),
  dealOwner: (id) => api.get(`/devis/${id}/deal-owner`),
  setDealOwner: (id, user_id) => api.post(`/devis/${id}/deal-owner`, { user_id }),
  setDealOwnerBulk: (ids, user_id) => api.post('/devis/hubspot/deal-owner-bulk', { ids, user_id }),
  refreshDealBulk: (ids) => api.post('/devis/hubspot/refresh-bulk', { ids }),
  // Résumé « intelligence » (synthèse HubSpot + suggestions, règles). refresh=true force la reconstruction.
  intelligence: (id, refresh = false) => api.get(`/devis/${id}/intelligence`, { params: refresh ? { refresh: true } : {}, timeout: 60000 }),
  win: (id) => api.post(`/devis/${id}/gagner`),
  lose: (id, data) => api.post(`/devis/${id}/perdre`, data),
  // Motifs de perte (alignés HubSpot closed_lost_reason) — source du dropdown « Marquer perdu ».
  lostReasons: () => api.get('/devis/lost-reasons'),
  reopen: (id) => api.post(`/devis/${id}/rouvrir`),
  // Reporte la date d'expiration d'un devis envoyé (sans le renvoyer) :
  // { days } (+N jours, 120 par défaut) ou { date_expiration } (report manuel).
  prolonger: (id, data) => api.post(`/devis/${id}/prolonger`, data || {}),
  createOrder: (id) => api.post(`/devis/${id}/creer-commande`),
  downloadPdf: (id) => api.get(`/devis/${id}/pdf`, { responseType: 'blob' }),
  // Suivi (tracking) : rollup + historique complet + chaleur des sections.
  tracking: (id) => api.get(`/devis/${id}/tracking`),
  resetTracking: (id) => api.post(`/devis/${id}/tracking/reset`),
  // Séquences de relance (auto avec validation).
  pendingRelances: () => api.get('/devis/relances/pending'),
  devisRelances: (id) => api.get(`/devis/${id}/relances`),
  editRelance: (relanceId, data) => api.put(`/devis/relances/${relanceId}`, data),
  sendRelance: (relanceId, data) => api.post(`/devis/relances/${relanceId}/envoyer`, data || {}),
  skipRelance: (relanceId) => api.post(`/devis/relances/${relanceId}/ignorer`),
  stopSequence: (id) => api.post(`/devis/${id}/sequence/stop`),
  setRelancesAuto: (id, enabled) => api.post(`/devis/${id}/relances-auto`, { enabled }),
  // stepIndex (0=Relance 1 … 3=Dernière) : choisit l'étape ; omis = étape courante.
  prepareRelance: (id, stepIndex) =>
    api.post(`/devis/${id}/relances/preparer`, stepIndex != null ? { step_index: stepIndex } : {}),
  catalogue: (activity, kind, search) => api.get('/devis/catalogue', { params: { activity, kind, search } }),
  dashboard: () => api.get('/devis/dashboard'),
  recomputeMargins: () => api.post('/devis/recompute-margins'),
  kpis: (id) => api.get(`/devis/${id}/kpis`),
  hubspotDealPrefill: (dealId) => api.get(`/devis/hubspot-deal/${dealId}/prefill`),
  // Backfill Source d'acquisition + Mode d'entrée sur les devis déjà liés à un deal.
  syncAcquisition: () => api.post('/devis/hubspot/sync-acquisition'),
  // Sécurisation : activer/désactiver le lien public de consultation.
  togglePublicLink: (id, disabled) => api.post(`/devis/${id}/public-link`, { disabled }),
  // Modèles de mail & de séquence (management only).
  getEmailTemplates: () => api.get('/devis/email-templates'),
  saveEmailTemplates: (data) => api.put('/devis/email-templates', data),
  // Lien de consultation client — pas d'authentification, le jeton est la
  // créance (voir routes/devis.py::_devis_view_token).
  getPublic: (token, preview = false) => api.get(`/devis/public/${token}${preview ? '?preview=1' : ''}`, { _skipAuthRedirect: true }),
  // inline:true → le PDF est chargé pour AFFICHAGE (visualiseur), pas compté
  // comme téléchargement. preview:true → aperçu commercial, jamais suivi.
  // v : empreinte de version du PDF (renvoyée par getPublic → data.pdf_version).
  // Présente sur l'URL du visualiseur → le PDF figé est mis en cache dur côté
  // navigateur (immutable), réouverture instantanée, re-téléchargement auto après
  // une modif (le v change). Absente → pas de cache dur (revalidation ETag).
  publicPdfUrl: (token, { inline = false, preview = false, v = null } = {}) => {
    const qs = [inline && 'inline=1', preview && 'preview=1', v && `v=${encodeURIComponent(v)}`].filter(Boolean).join('&');
    return `${API_BASE}/devis/public/${token}/pdf${qs ? `?${qs}` : ''}`;
  },
  // Balise de suivi (temps par page, téléchargement) — appelée par le visualiseur PDF.
  publicTrackUrl: (token) => `${API_BASE}/devis/public/${token}/track`,
  // Page web rendue par le même moteur que le PDF (contenu paramétré identique).
  publicViewUrl: (token) => `${API_BASE}/devis/public/${token}/view`,
  // Paramétrage des devis (images par activité / zone).
  getTemplate: (activity) => api.get(`/devis/templates/${activity}`),
  uploadTemplateImage: (activity, zone, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/devis/templates/${activity}/image/${zone}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteTemplateImage: (activity, zone) => api.delete(`/devis/templates/${activity}/image/${zone}`),
  saveTemplateContent: (activity, content) => api.put(`/devis/templates/${activity}/content`, { content }),
  // L'URL stockée est déjà « /api/devis/templates/asset/… » ; on la préfixe du backend.
  templateAssetUrl: (url) => (url ? `${BACKEND_URL}${url}` : null),
  // Photo produit (par ligne matériel) : upload -> { url } stocké dans mat.image.
  uploadProductPhoto: (file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post('/devis/products/photo', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  // L'URL stockée (/api/devis/products/photo/…) préfixée du backend pour l'aperçu.
  productPhotoUrl: (url) => (url ? (url.startsWith('data:') ? url : `${BACKEND_URL}${url}`) : null),
  // Éditeur de modèle (blocs ordonnés) par activité + aperçu live.
  getLayout: (activity) => api.get(`/devis/templates/${activity}/layout`),
  saveLayout: (activity, blocks) => api.put(`/devis/templates/${activity}/layout`, { blocks }),
  previewLayout: (activity, blocks) =>
    api.post(`/devis/templates/${activity}/preview`, { blocks }, { responseType: 'text' }),
  // Logos du bandeau « Nos marques » (LDWS + 4 marques), injectables au paramétrage.
  getBrandAssets: () => api.get('/devis/templates/brands'),
  uploadBrandAsset: (slot, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/devis/templates/brands/image/${slot}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteBrandAsset: (slot) => api.delete(`/devis/templates/brands/image/${slot}`),
};

// Modèles de devis (refonte modulaire) : plusieurs modèles nommés par activité,
// chacun = blocs ordonnés (HTML éditable + fond) + thème. CRUD + aperçu live.
export const devisModelsAPI = {
  list: (activity) => api.get('/devis-models', { params: activity ? { activity } : {} }),
  create: (data) => api.post('/devis-models', data),
  get: (id) => api.get(`/devis-models/${id}`),
  update: (id, data) => api.put(`/devis-models/${id}`, data),
  delete: (id) => api.delete(`/devis-models/${id}`),
  listDeleted: () => api.get('/devis-models/corbeille'),
  // Restaure un MODÈLE supprimé (corbeille). Nom distinct de `restore` (versions)
  // ci-dessous : deux clés `restore` dans le même objet s'écrasaient et la
  // restauration de la corbeille tapait la route des versions (→ 404).
  restoreDeleted: (id) => api.post(`/devis-models/${id}/restore`),
  duplicate: (id) => api.post(`/devis-models/${id}/duplicate`),
  // Aperçu live : on peut passer les blocs/thème NON enregistrés pour prévisualiser.
  preview: (id, override) => api.post(`/devis-models/${id}/preview`, override || {}, { responseType: 'text' }),
  // Aperçu « nouvel onglet » du modèle enregistré : on récupère un jeton puis on
  // ouvre l'URL de vue (le jeton fait office de créance, pas d'entête d'auth).
  previewLink: (id) => api.get(`/devis-models/${id}/preview-link`),
  previewViewUrl: (token) => `${API_BASE}/devis-models/preview-view/${token}`,
  previewPdfUrl: (token) => `${API_BASE}/devis-models/preview-pdf/${token}`,
  // Import / export & usage
  import: (data) => api.post('/devis-models/import', data),
  usage: (id) => api.get(`/devis-models/${id}/usage`),
  // Bibliothèque d'images (fonds, illustrations, logos réutilisables)
  libraryList: () => api.get('/devis-models/library/list'),
  libraryUpload: (file, name) => {
    const fd = new FormData();
    fd.append('file', file);
    if (name) fd.append('name', name);
    return api.post('/devis-models/library/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  libraryDelete: (id) => api.delete(`/devis-models/library/${id}`),
  // Bibliothèque de blocs réutilisables
  blockSave: (data) => api.post('/devis-models/blocks/save', data),
  blockList: (activity) => api.get('/devis-models/blocks/list', { params: activity ? { activity } : {} }),
  blockDelete: (id) => api.delete(`/devis-models/blocks/${id}`),
  // Thèmes réutilisables
  themeSave: (data) => api.post('/devis-models/themes/save', data),
  themeList: () => api.get('/devis-models/themes/list'),
  themeDelete: (id) => api.delete(`/devis-models/themes/${id}`),
  // Versionnage
  versions: (id) => api.get(`/devis-models/${id}/versions`),
  restore: (id, versionId) => api.post(`/devis-models/${id}/restore/${versionId}`),
  // L'URL stockée (/api/devis-models/library/file/…) préfixée du backend pour l'aperçu <img>.
  libraryImageUrl: (url) => (url ? (url.startsWith('data:') || url.startsWith('http') ? url : `${BACKEND_URL}${url}`) : null),
  // Métadonnées de l'éditeur : blocs par défaut, clés d'application, variables.
  meta: (activity) => api.get(`/devis-models/meta/${activity}`),
  // HTML par défaut (codé) d'un bloc — pour l'éditer comme point de départ.
  blockDefaultHtml: (activity, blockType) =>
    api.get(`/devis-models/meta/${activity}/block-html/${blockType}`, { responseType: 'text' }),
  // Gabarit « Proposition & tarifs » par défaut (éditable) pour un nouveau gabarit de sous-catégorie.
  propositionDefault: (activity) =>
    api.get(`/devis-models/meta/${activity}/proposition-default`, { responseType: 'text' }),
};

export const commissionsAPI = {
  list: (params) => api.get('/commissions', { params }),
  get: (id) => api.get(`/commissions/${id}`),
  stats: (params) => api.get('/commissions/stats', { params }),
  dashboard: (params) => api.get('/commissions/dashboard', { params }),
  preview: (contractId, params) => api.get(`/commissions/preview/${contractId}`, { params }),
  create: (data) => api.post('/commissions', data),
  update: (id, data) => api.patch(`/commissions/${id}`, data),
  delete: (id) => api.delete(`/commissions/${id}`),
  markPaid: (id, data) => api.post(`/commissions/${id}/mark-paid`, data),
  recalculate: (orderId) => api.post(`/commissions/recalculate/${orderId}`),
  hubspotUnimportedDeals: (ownerId) => api.get('/commissions/hubspot-unimported-deals', { params: { hubspot_owner_id: ownerId } }),
  contractCommercialSummary: (contractId) => api.get(`/commissions/contract/${contractId}/commercial-summary`),
  simulate: (body) => api.post('/commissions/simulate', body),
  commercialRoi: (userId) => api.get(`/commissions/commercial/${userId}/roi`),
};

export const commissionPlansAPI = {
  list: () => api.get('/commission-plans'),
  get: (id) => api.get(`/commission-plans/${id}`),
  create: (data) => api.post('/commission-plans', data),
  update: (id, data) => api.put(`/commission-plans/${id}`, data),
  delete: (id) => api.delete(`/commission-plans/${id}`),
  exportPdf: (id) => api.get(`/commission-plans/${id}/pdf`, { responseType: 'blob' }),
};

export const commissionConfigsAPI = {
  list: () => api.get('/commission-configs'),
  get: (userId) => api.get(`/commission-configs/${userId}`),
  upsert: (userId, data) => api.put(`/commission-configs/${userId}`, data),
  delete: (userId) => api.delete(`/commission-configs/${userId}`),
  autoLinkHubspot: () => api.post('/commission-configs/auto-link-hubspot'),
};

export default api;
